import type React from 'react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

/* ==========================================================================
   ROOM SEQUENCE: show night on the home page, as three real moments of one
   room, each shot on a fan's phone and the room's TV at the same time by
   scripts/capture-marketing.ts ("room-sequence"). The visitor steps through
   them with the site's own buttons, which say what the fan does next.

   Every frame is the product. An earlier version let the visitor type their
   own message over a screenshot; it was livelier and it was a mock-up, so it
   went.
   ========================================================================== */

export interface RoomFrame {
  tv: string;
  phone: string;
  /** What the fan has just done, read out for screen readers and shown under the frames. */
  caption: string;
}

export const RoomSequence: React.FC<{
  frames: RoomFrame[];
  /** The button that moves from frame i to frame i + 1. */
  steps: string[];
  className?: string;
}> = ({ frames, steps, className }) => {
  const [at, setAt] = useState(0);
  const last = at === frames.length - 1;

  return (
    // From sm up the TV takes the full width and the phone hangs off its
    // lower-left corner, over the lineup strip, leaving the chat column (where
    // the fan's message lands) and the act's name clear. On a phone they stack.
    <div className={cn('relative sm:pb-[30%]', className)}>
      <div className="rounded-md bg-ink p-1.5 shadow-xl sm:p-2">
        <div className="relative overflow-hidden rounded-[3px]">
          {frames.map((f, i) => (
            <img
              key={f.tv}
              src={f.tv}
              alt={i === at ? `The room's TV: ${f.caption}` : ''}
              aria-hidden={i !== at}
              width={3840}
              height={2160}
              loading="lazy"
              className={cn(
                'block w-full h-auto transition-opacity duration-300 motion-reduce:transition-none',
                i === 0 ? 'relative' : 'absolute inset-0',
                i === at ? 'opacity-100' : 'opacity-0',
              )}
            />
          ))}
        </div>
      </div>

      <div className="mx-auto mt-4 w-[180px] overflow-hidden rounded-[1.4rem] border-[5px] border-ink bg-ink shadow-2xl sm:absolute sm:left-[-3%] sm:top-[52%] sm:mt-0 sm:w-[24%] sm:min-w-[150px] sm:max-w-[200px]">
        <div className="relative">
          {frames.map((f, i) => (
            <img
              key={f.phone}
              src={f.phone}
              alt={i === at ? `A fan's phone: ${f.caption}` : ''}
              aria-hidden={i !== at}
              width={824}
              height={1740}
              loading="lazy"
              className={cn(
                'block w-full h-auto transition-opacity duration-300 motion-reduce:transition-none',
                i === 0 ? 'relative' : 'absolute inset-0',
                i === at ? 'opacity-100' : 'opacity-0',
              )}
            />
          ))}
        </div>
      </div>

      <div className="mt-4 sm:ml-[27%]">
        <p className="text-sm text-grey-700" aria-live="polite">{frames[at].caption}</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {last ? (
            <button
              type="button"
              onClick={() => setAt(0)}
              className="rounded-md border border-grey-300 px-4 py-2 text-sm font-medium text-ink hover:border-grey-500"
            >
              Start over
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setAt(at + 1)}
              className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-grey-50 hover:bg-grey-800"
            >
              {steps[at]}
            </button>
          )}
          <span className="label-stamp text-grey-500">
            {at + 1} of {frames.length} · real screens from a demo show
          </span>
        </div>
      </div>
    </div>
  );
};
